/**
 * Who may **retry a FAILED delivery** from the delivery detail page
 * (`POST /mgmt/v1/deliveries/:uid/retry`).
 *
 * `REACT_APP_DELIVERY_RETRY_EMAILS`: comma-separated addresses, case- and
 * whitespace-insensitive. Unset / empty means **nobody** — the allowlist fails
 * closed on purpose, same as the UJP approver list next door. No `all` escape
 * hatch: the retry rewrites a settled status and fans out to shipment and
 * outbound sync, so each operator is named.
 *
 * ⚠️ Same caveat as `ujp-permissions.ts`: this is a UI affordance, not a
 * security boundary — the bundle is public and the value baked in at build
 * time. The write itself is guarded by delivery-service (WEB identity + the
 * retry eligibility rule; COD/QRIS/non-FAILED are refused server-side
 * regardless of who clicks).
 */

const parseAllowlist = (raw: string): Set<string> =>
  new Set(
    raw
      .split(',')
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean)
  );

const RETRIERS = parseAllowlist(
  process.env.REACT_APP_DELIVERY_RETRY_EMAILS ?? ''
);

/** May this user retry a FAILED delivery? */
export const canRetryDelivery = (email?: string | null): boolean => {
  if (!email) return false;
  return RETRIERS.has(email.trim().toLowerCase());
};

/** True when nobody is allowed — the button never renders (fail closed). */
export const isRetryAllowlistEmpty = (): boolean => RETRIERS.size === 0;

/** The configured addresses, for a "who can do this?" hint in the UI. */
export const deliveryRetryEmails = (): string[] =>
  Array.from(RETRIERS).sort();
